// React
import {
  FunctionComponent,
  JSXElementConstructor,
  ReactElement,
  useEffect,
} from "react";
// Components
import { Page, PageConfiguration } from "@fyrstain/hl7-front-library";
// Translation
import i18n from "i18next";
import { useTranslation } from "react-i18next";
// Authentication
import UserService from "../../services/UserService";
import { toPublicUrl } from "../../services/PublicUrl";

const JunoPage: FunctionComponent<{
  // The title of the page
  titleKey?: string;
  // The loading state of the page
  loading?: boolean;
  // The content of the page
  children?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
  // Fit the footer to the bottom of the page
  fitFooter?: boolean;
  // If the page needs login or not
  needsLogin: boolean;
}> = (props) => {
  useTranslation();

  /////////////////////////////////
  //           METHODS           //
  /////////////////////////////////

  const handleLangChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(event.target.value);
  };

  useEffect(() => {
    const navbarLogo = document.querySelector<HTMLAnchorElement>(
      "a.navbar-brand[href='/']"
    );

    if (navbarLogo) {
      navbarLogo.href = toPublicUrl("/Home");
    }
  });

  const fullConfig: PageConfiguration = {
    // Translation
    language: i18n.t,
    navigationBarConfigs: {
      // Application logo
      applicationItems: [
        {
          logoLink:
            "https://integ.fyrstain.com/assets/ApplicationsLogos/Pandora.png",
          link: "https://integ.fyrstain.com/Pandora/Home",
          alt: "Pandora logo",
        },
      ],
      // Juno logo
      logoLink: toPublicUrl("/assets/Junologo.png"),
      alt: "Juno Logo",
      // Authentication
      authentication: {
        doLogin: UserService.doLogin,
        doLogout: UserService.doLogout,
        isAuthenticated: () => UserService.isAuthenticated() || false,
        getUserName: () => UserService.getUsername(),
      },
      // the menu items with their subItems who contains the navigation to the differents pages
      menuItems: [
        {
          title: i18n.t("navbar.items.individuals"),
          subItems: [
            {
              title: i18n.t("label.users"),
              link: toPublicUrl("/Individuals/users"),
            },
            {
              title: i18n.t("label.patients"),
              link: toPublicUrl("/Individuals/patients"),
            },
            {
              title: i18n.t("label.practitioners"),
              link: toPublicUrl("/Individuals/practitioners"),
            },
          ],
        },
        {
          title: i18n.t("navbar.items.structure"),
          subItems: [
            {
              title: i18n.t("navbar.items.organizations"),
              link: toPublicUrl("/Organizations"),
            },
            {
              title: i18n.t("navbar.items.organizationstructure"),
              link: toPublicUrl("/OrganizationStructure"),
            },
            {
              title: i18n.t("navbar.items.healthcareservices"),
              link: toPublicUrl("/HealthcareServices"),
            },
            {
              title: i18n.t("navbar.items.healthcareservicestructure"),
              link: toPublicUrl("/HealthcareServiceStructure"),
            },
            {
              title: i18n.t("navbar.items.locations"),
              link: toPublicUrl("/Locations"),
            },
            {
              title: i18n.t("navbar.items.locationstructure"),
              link: toPublicUrl("/LocationStructure"),
            },
          ],
        },
      ],
      // the user items
      // Admin, Login, Logout are the default items
      dropDownItems: [
        {
          title: "Admin",
          link: toPublicUrl("/InProgress"),
        },
      ],
      // customItems: <ServerUrlField />
    },
    // the title of the page
    titleKey: props.titleKey,
    // the loading state of the page
    loading: props.loading,
    // the content of the page
    children: props.children,
    // if the page needs login or not
    needsLogin: props.needsLogin,
    isAuthenticated: () => UserService.isAuthenticated() || false,
    doLogin: UserService.doLogin,
    footerConfigs: {
      languages: {
        default: i18n.language,
        onChange: handleLangChange,
        options: [
          {
            label: "English",
            value: "en",
          },
          {
            label: "Français",
            value: "fr",
          },
        ],
      },
      logo: [
        {
          logoLink:
            "https://fyrstain.com/wp-content/uploads/2022/10/Logo_fyrstain_horyzontal.svg",
          alt: "Horizontal logo type",
          link: "https://fyrstain.com",
        },
        ...(process.env.REACT_APP_DISPLAY_CLIENT_LOGO === "true"
          ? [
              {
                logoLink: toPublicUrl("/assets/HL7-EU-Logo.jpg"),
                alt: "Horizontal logo type",
                link: "https://hl7.eu",
              },
            ]
          : []),
      ],
      items: [
        {
          label: i18n.t("footer.items.about"),
          link: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.contact"),
          link: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.problemtracking"),
          link: toPublicUrl("/InProgress"),
        },
        {
          label: i18n.t("footer.items.implementationguide"),
          link: toPublicUrl("/ImplementationGuide"),
        },
      ],
    },
  };
  return <Page {...fullConfig} />;
};

export default JunoPage;
